import React, { useMemo, useState, useContext, createElement, Component } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FileText, Download, Search, Calendar, Filter, Eye, Settings, ChevronDown, Copy, Check, X, Clock, User, AlertCircle, CheckCircle, XCircle, Activity, ArrowRight } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppBadge } from '../components/base/AppBadge';
import { AppInput } from '../components/base/AppInput';
import { Toast } from '../components/Toast';
import { useAgent } from '../contexts/AgentContext';
// Types
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userEmail: string;
  serviceId: string;
  serviceName: string;
  eventType: 'Query' | 'Response' | 'Error' | 'Policy Violation' | 'System Event';
  severity: 'Info' | 'Warning' | 'Critical';
  inputPreview: string;
  outputPreview: string;
  duration: number;
  status: 'Success' | 'Failed' | 'Partial';
  tokenUsage: number;
  cost: number;
}
interface LogDetails extends AuditLog {
  fullInput: string;
  fullOutput: string;
  metadata: {
    modelUsed: string;
    toolsInvoked: string[];
    knowledgeSources: string[];
    requestHeaders?: Record<string, string>;
    ip?: string;
    userAgent?: string;
  };
  trace: Array<{
    step: string;
    timestamp: string;
    duration: number;
    status: string;
  }>;
}
// Helper Functions
function isToday(date: Date): boolean {
  const today = new Date();
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
}
function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}
function convertToCSV(logs: AuditLog[]): string {
  const headers = ['ID', 'Timestamp', 'User', 'Email', 'Service', 'Event Type', 'Status', 'Duration (ms)', 'Tokens', 'Cost'];
  const rows = logs.map(log => [log.id, log.timestamp, log.userName, log.userEmail, log.serviceName, log.eventType, log.status, log.duration, log.tokenUsage, log.cost]);
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}
function generateMockLogs(): AuditLog[] {
  const logs: AuditLog[] = [];
  const eventTypes: AuditLog['eventType'][] = ['Query', 'Response', 'Error', 'Policy Violation', 'System Event'];
  const severities: AuditLog['severity'][] = ['Info', 'Warning', 'Critical'];
  const statuses: AuditLog['status'][] = ['Success', 'Failed', 'Partial'];
  const services = ['Service 1', 'Service 2', 'Service 3'];
  const users = [{
    name: 'Mario Rossi',
    email: 'mario.rossi@example.com'
  }, {
    name: 'Laura Bianchi',
    email: 'laura.bianchi@example.com'
  }, {
    name: 'Giuseppe Verdi',
    email: 'giuseppe.verdi@example.com'
  }];
  for (let i = 0; i < 150; i++) {
    const user = users[Math.floor(Math.random() * users.length)];
    const service = services[Math.floor(Math.random() * services.length)];
    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const date = new Date();
    date.setHours(date.getHours() - Math.floor(Math.random() * 72));
    logs.push({
      id: `log-${i + 1}`,
      timestamp: date.toISOString(),
      userId: `user-${Math.floor(Math.random() * 3) + 1}`,
      userName: user.name,
      userEmail: user.email,
      serviceId: `service-${Math.floor(Math.random() * 3) + 1}`,
      serviceName: service,
      eventType,
      severity: severities[Math.floor(Math.random() * severities.length)],
      inputPreview: `Query about ${['product info', 'pricing', 'support', 'features'][Math.floor(Math.random() * 4)]}`,
      outputPreview: `Response with ${['detailed info', 'pricing details', 'support guidance', 'feature list'][Math.floor(Math.random() * 4)]}`,
      duration: Math.floor(Math.random() * 5000) + 100,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      tokenUsage: Math.floor(Math.random() * 2000) + 100,
      cost: Math.random() * 0.5
    });
  }
  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
function generateLogDetails(log: AuditLog): LogDetails {
  return {
    ...log,
    fullInput: `Full input text for ${log.inputPreview}. This would contain the complete user query with all context and parameters.`,
    fullOutput: `Full output text for ${log.outputPreview}. This would contain the complete AI response with all details and formatting.`,
    metadata: {
      modelUsed: 'gpt-4-turbo',
      toolsInvoked: ['search', 'calculator', 'database_query'],
      knowledgeSources: ['internal_docs', 'public_api', 'knowledge_base'],
      requestHeaders: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ***'
      },
      ip: '192.168.1.1',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)'
    },
    trace: [{
      step: 'Request Received',
      timestamp: log.timestamp,
      duration: 5,
      status: 'Success'
    }, {
      step: 'Policy Check',
      timestamp: new Date(new Date(log.timestamp).getTime() + 5).toISOString(),
      duration: 15,
      status: 'Success'
    }, {
      step: 'Model Inference',
      timestamp: new Date(new Date(log.timestamp).getTime() + 20).toISOString(),
      duration: log.duration - 50,
      status: 'Success'
    }, {
      step: 'Response Sent',
      timestamp: new Date(new Date(log.timestamp).getTime() + log.duration).toISOString(),
      duration: 10,
      status: 'Success'
    }]
  };
}
// Log Details Modal Component
interface LogDetailsModalProps {
  log: LogDetails;
  activeTab: 'overview' | 'request' | 'response' | 'trace' | 'metadata';
  onTabChange: (tab: 'overview' | 'request' | 'response' | 'trace' | 'metadata') => void;
  onClose: () => void;
}
function LogDetailsModal({
  log,
  activeTab,
  onTabChange,
  onClose
}: LogDetailsModalProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1682">
      <div className="bg-[#0a0a0a] rounded-2xl border border-white/[0.08] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1683">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-1684">
          <div data-id="element-1685">
            <h2 className="text-[17px] font-semibold text-white" data-id="element-1686">
              Log Details
            </h2>
            <p className="text-[12px] text-white/40 mt-1" data-id="element-1687">{log.id}</p>
          </div>
          <button onClick={onClose} className="p-2 text-white\\/40 hover:text-white\\/70 hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1688">
            <X className="w-5 h-5" data-id="element-1689" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-white/[0.06]" data-id="element-1690">
          <div className="flex space-x-1" data-id="element-1691">
            {[{
            id: 'overview',
            label: 'Overview'
          }, {
            id: 'request',
            label: 'Request'
          }, {
            id: 'response',
            label: 'Response'
          }, {
            id: 'trace',
            label: 'Trace'
          }, {
            id: 'metadata',
            label: 'Metadata'
          }].map(tab => <button key={tab.id} onClick={() => onTabChange(tab.id as any)} className={`px-4 py-3 text-[12px] font-medium transition-all relative ${activeTab === tab.id ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-1692">
                {tab.label}
                {activeTab === tab.id && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1693" />}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-1694">
          {activeTab === 'overview' && <div className="space-y-6" data-id="element-1695">
              <div className="grid grid-cols-2 gap-4" data-id="element-1696">
                <div data-id="element-1697">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1698">Timestamp</p>
                  <p className="text-[12px] text-white" data-id="element-1699">
                    {formatDate(log.timestamp)}
                  </p>
                </div>
                <div data-id="element-1700">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1701">Duration</p>
                  <p className="text-[12px] text-white" data-id="element-1702">{log.duration}ms</p>
                </div>
                <div data-id="element-1703">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1704">User</p>
                  <p className="text-[12px] text-white" data-id="element-1705">{log.userName}</p>
                  <p className="text-[10px] text-white/40" data-id="element-1706">{log.userEmail}</p>
                </div>
                <div data-id="element-1707">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1708">Service</p>
                  <p className="text-[12px] text-white" data-id="element-1709">{log.serviceName}</p>
                </div>
                <div data-id="element-1710">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1711">Event Type</p>
                  <AppBadge variant="blue" data-id="element-1712">{log.eventType}</AppBadge>
                </div>
                <div data-id="element-1713">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1714">Status</p>
                  <AppBadge variant={log.status === 'Success' ? 'green' : log.status === 'Failed' ? 'red' : 'amber'} data-id="element-1715">
                    {log.status}
                  </AppBadge>
                </div>
                <div data-id="element-1716">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1717">Token Usage</p>
                  <p className="text-[12px] text-white" data-id="element-1718">{log.tokenUsage}</p>
                </div>
                <div data-id="element-1719">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1720">Cost</p>
                  <p className="text-[12px] text-white" data-id="element-1721">
                    ${log.cost.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>}

          {activeTab === 'request' && <div data-id="element-1722">
              <div className="flex items-center justify-between mb-4" data-id="element-1723">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-1724">
                  Full Request
                </h3>
                <button onClick={() => handleCopy(log.fullInput)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-white\\/40 hover:text-white\\/70 bg-white\\/[0.03] hover:bg-white\\/[0.06] rounded-lg transition-all" data-id="element-1725">
                  {copied ? <>
                      <Check className="w-3 h-3" data-id="element-1726" />
                      Copied
                    </> : <>
                      <Copy className="w-3 h-3" data-id="element-1727" />
                      Copy
                    </>}
                </button>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1728">
                <pre className="text-[12px] text-white/70 whitespace-pre-wrap font-mono" data-id="element-1729">
                  {log.fullInput}
                </pre>
              </div>
            </div>}

          {activeTab === 'response' && <div data-id="element-1730">
              <div className="flex items-center justify-between mb-4" data-id="element-1731">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-1732">
                  Full Response
                </h3>
                <button onClick={() => handleCopy(log.fullOutput)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-white\\/40 hover:text-white\\/70 bg-white\\/[0.03] hover:bg-white\\/[0.06] rounded-lg transition-all" data-id="element-1733">
                  {copied ? <>
                      <Check className="w-3 h-3" data-id="element-1734" />
                      Copied
                    </> : <>
                      <Copy className="w-3 h-3" data-id="element-1735" />
                      Copy
                    </>}
                </button>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1736">
                <pre className="text-[12px] text-white/70 whitespace-pre-wrap font-mono" data-id="element-1737">
                  {log.fullOutput}
                </pre>
              </div>
            </div>}

          {activeTab === 'trace' && <div className="space-y-3" data-id="element-1738">
              {log.trace.map((step, index) => <div key={index} className="flex items-start gap-4 p-4 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1739">
                  <div className="flex-shrink-0" data-id="element-1740">
                    {step.status === 'Success' ? <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-1741" /> : <XCircle className="w-5 h-5 text-rose-400" data-id="element-1742" />}
                  </div>
                  <div className="flex-1" data-id="element-1743">
                    <div className="flex items-center justify-between mb-2" data-id="element-1744">
                      <h4 className="text-[12px] font-medium text-white" data-id="element-1745">
                        {step.step}
                      </h4>
                      <span className="text-[10px] text-white/40" data-id="element-1746">
                        {step.duration}ms
                      </span>
                    </div>
                    <p className="text-[10px] text-white/40" data-id="element-1747">
                      {formatDate(step.timestamp)}
                    </p>
                  </div>
                </div>)}
            </div>}

          {activeTab === 'metadata' && <div className="space-y-6" data-id="element-1748">
              <div data-id="element-1749">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1750">
                  Model Information
                </h3>
                <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1751">
                  <p className="text-[12px] text-white" data-id="element-1752">
                    {log.metadata.modelUsed}
                  </p>
                </div>
              </div>

              <div data-id="element-1753">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1754">
                  Tools Invoked
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1755">
                  {log.metadata.toolsInvoked.map(tool => <AppBadge key={tool} variant="blue" data-id="element-1756">
                      {tool}
                    </AppBadge>)}
                </div>
              </div>

              <div data-id="element-1757">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1758">
                  Knowledge Sources
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1759">
                  {log.metadata.knowledgeSources.map(source => <AppBadge key={source} variant="default" data-id="element-1760">
                      {source}
                    </AppBadge>)}
                </div>
              </div>

              {log.metadata.ip && <div data-id="element-1761">
                  <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1762">
                    Request Info
                  </h3>
                  <div className="space-y-2" data-id="element-1763">
                    <div className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1764">
                      <span className="text-[12px] text-white/40" data-id="element-1765">
                        IP Address
                      </span>
                      <span className="text-[12px] text-white" data-id="element-1766">
                        {log.metadata.ip}
                      </span>
                    </div>
                    {log.metadata.userAgent && <div className="p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1767">
                        <p className="text-[12px] text-white/40 mb-1" data-id="element-1768">
                          User Agent
                        </p>
                        <p className="text-[10px] text-white/70 break-all" data-id="element-1769">
                          {log.metadata.userAgent}
                        </p>
                      </div>}
                  </div>
                </div>}
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end" data-id="element-1770">
          <AppButton variant="secondary" onClick={onClose} data-id="element-1771">
            Close
          </AppButton>
        </div>
      </div>
    </div>;
}
// Retention Policy Modal Component
interface RetentionPolicyModalProps {
  onClose: () => void;
  onSave: () => void;
}
function RetentionPolicyModal({
  onClose,
  onSave
}: RetentionPolicyModalProps) {
  const [retentionDays, setRetentionDays] = useState('90');
  const [autoArchive, setAutoArchive] = useState(true);
  const [archiveLocation, setArchiveLocation] = useState('s3');
  const handleSave = () => {
    onSave();
  };
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1772">
      <div className="bg-[#0a0a0a] rounded-2xl border border-white/[0.08] w-full max-w-2xl" data-id="element-1773">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-1774">
          <div data-id="element-1775">
            <h2 className="text-[17px] font-semibold text-white" data-id="element-1776">
              Retention Policy
            </h2>
            <p className="text-[12px] text-white/40 mt-1" data-id="element-1777">
              Configura la policy di conservazione dei log
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-white\\/40 hover:text-white\\/70 hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1778">
            <X className="w-5 h-5" data-id="element-1779" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-1780">
          <div data-id="element-1781">
            <label className="block text-[12px] font-medium text-white/70 mb-2" data-id="element-1782">
              Periodo di Conservazione (giorni)
            </label>
            <AppInput type="number" value={retentionDays} onChange={e => setRetentionDays(e.target.value)} placeholder="90" data-id="element-1783" />
            <p className="text-[10px] text-white/40 mt-2" data-id="element-1784">
              I log più vecchi di questo periodo verranno automaticamente
              archiviati
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1785">
            <div data-id="element-1786">
              <p className="text-[12px] font-medium text-white" data-id="element-1787">
                Archiviazione Automatica
              </p>
              <p className="text-[10px] text-white/40 mt-1" data-id="element-1788">
                Archivia automaticamente i log scaduti
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-1789">
              <input type="checkbox" checked={autoArchive} onChange={e => setAutoArchive(e.target.checked)} className="sr-only peer" data-id="element-1790" />
              <div className="w-11 h-6 bg-white\\/[0.08] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-\\[#8eff01]\\/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-\\[#8eff01]\\" data-id="element-1791"></div>
            </label>
          </div>

          {autoArchive && <div data-id="element-1792">
              <label className="block text-[12px] font-medium text-white/70 mb-2" data-id="element-1793">
                Destinazione Archivio
              </label>
              <select value={archiveLocation} onChange={e => setArchiveLocation(e.target.value)} className="w-full bg-white\\/[0.03] border border-white\\/[0.08] text-white px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-\\[#8eff01]\\/50" data-id="element-1794">
                <option value="s3" data-id="element-1795">Amazon S3</option>
                <option value="azure" data-id="element-1796">Azure Blob Storage</option>
                <option value="gcs" data-id="element-1797">Google Cloud Storage</option>
                <option value="local" data-id="element-1798">Local Storage</option>
              </select>
            </div>}

          <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-id="element-1799">
            <div className="flex items-start gap-3" data-id="element-1800">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-1801" />
              <div data-id="element-1802">
                <p className="text-[12px] font-medium text-white mb-1" data-id="element-1803">
                  Attenzione
                </p>
                <p className="text-[12px] text-white/40" data-id="element-1804">
                  La modifica della retention policy potrebbe influenzare la
                  conformità normativa. Assicurati di rispettare i requisiti
                  GDPR e AI ACT.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end gap-3" data-id="element-1805">
          <AppButton variant="secondary" onClick={onClose} data-id="element-1806">
            Annulla
          </AppButton>
          <AppButton variant="primary" onClick={handleSave} data-id="element-1807">
            Salva Policy
          </AppButton>
        </div>
      </div>
    </div>;
}
// Main Component
interface AuditTrailPageProps {
  hideHeader?: boolean;
  serviceScope?: string | null;
}
export function AuditTrailPage({
  hideHeader = false,
  serviceScope
}: AuditTrailPageProps) {
  // Context
  const {
    selectedServiceId,
    setSelectedServiceId
  } = useAgent();
  const location = useLocation();
  // Usa serviceScope da prop, oppure da context
  const effectiveServiceScope = serviceScope !== undefined ? serviceScope : selectedServiceId;
  const isServiceScoped = !!effectiveServiceScope;
  // Helper function to get service name
  const getServiceName = (serviceId: string | null) => {
    if (!serviceId) return 'Unknown';
    const serviceMap: Record<string, string> = {
      'service-1': 'Service 1',
      'service-2': 'Service 2',
      'service-3': 'Service 3',
      'service-4': 'Service 4',
      'service-5': 'Service 5'
    };
    return serviceMap[serviceId] || `Service ${serviceId}`;
  };
  // State
  const [allLogs] = useState<AuditLog[]>(generateMockLogs());
  const [selectedLog, setSelectedLog] = useState<LogDetails | null>(null);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<'overview' | 'request' | 'response' | 'trace' | 'metadata'>('overview');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<string>('All');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('All');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 50;
  // Filtered logs with SERVICE SCOPE as primary filter
  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      // PRIMARY FILTER: Service scope
      if (isServiceScoped && effectiveServiceScope) {
        if (log.serviceId !== effectiveServiceScope) return false;
      }
      // Secondary filters (esistenti)
      if (searchQuery && !log.inputPreview.toLowerCase().includes(searchQuery.toLowerCase()) && !log.outputPreview.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedEventType !== 'All' && log.eventType !== selectedEventType) return false;
      if (selectedSeverity !== 'All' && log.severity !== selectedSeverity) return false;
      if (selectedServices.length > 0 && !selectedServices.includes(log.serviceId)) return false;
      if (selectedUsers.length > 0 && !selectedUsers.includes(log.userId)) return false;
      return true;
    });
  }, [allLogs, isServiceScoped, effectiveServiceScope, searchQuery, selectedEventType, selectedSeverity, selectedServices, selectedUsers]);
  // Paginated logs
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * logsPerPage;
    return filteredLogs.slice(startIndex, startIndex + logsPerPage);
  }, [filteredLogs, currentPage]);
  // Stats
  const stats = useMemo(() => ({
    totalLogs: filteredLogs.length,
    todayActivity: filteredLogs.filter(l => isToday(new Date(l.timestamp))).length,
    avgResponseTime: filteredLogs.length > 0 ? (filteredLogs.reduce((sum, l) => sum + l.duration, 0) / filteredLogs.length / 1000).toFixed(1) : '0.0',
    complianceScore: 98
  }), [filteredLogs]);
  // Handlers
  const handleExport = (format: 'csv' | 'json') => {
    const dataStr = format === 'json' ? JSON.stringify(filteredLogs, null, 2) : convertToCSV(filteredLogs);
    const blob = new Blob([dataStr], {
      type: format === 'json' ? 'application/json' : 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-trail-${new Date().toISOString().split('T')[0]}.${format}`;
    link.click();
    setToast({
      message: `Export ${format.toUpperCase()} completato`,
      type: 'success'
    });
  };
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(generateLogDetails(log));
  };
  const handleResetFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setSelectedServices([]);
    setSelectedUsers([]);
    setSelectedEventType('All');
    setSelectedSeverity('All');
  };
  return <div className="min-h-screen bg-[#121212]" data-id="element-1808">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1809" />}

      {/* Header */}
      {!hideHeader && <div className="pt-6 px-4 md:px-8 pb-4" data-id="element-1810">
          <h1 className="text-[20px] font-semibold text-white tracking-tight" data-id="element-1811">
            Audit Trail
          </h1>
          <p className="text-[13px] text-white/40 mt-1" data-id="element-1812">
            Log immutabile delle interazioni AI
          </p>
        </div>}

      {/* Service-Scoped Banner */}
      {isServiceScoped && effectiveServiceScope && <div className="bg-[#8eff01]/5 border border-[#8eff01]/20 rounded-xl p-4 flex items-center justify-between" data-id="element-1813">
          <div className="flex items-center gap-3" data-id="element-1814">
            <div className="p-2 bg-[#8eff01]/10 rounded-lg" data-id="element-1815">
              <Filter className="w-5 h-5 text-[#8eff01]" data-id="element-1816" />
            </div>
            <div data-id="element-1817">
              <p className="text-[12px] font-medium text-white" data-id="element-1818">
                Viewing: {getServiceName(effectiveServiceScope)}
              </p>
              <p className="text-[10px] text-white/40 mt-0.5" data-id="element-1819">
                Showing data for this service only
              </p>
            </div>
          </div>
          <Link to="/audit-trail" onClick={() => setSelectedServiceId(null)} className="flex items-center gap-2 px-4 py-2 text-[12px] font-medium text-\\[#8eff01\\] hover:text-\\[#8eff01\\] hover:bg-\\[#8eff01\\]\\/10 rounded-lg transition-all" data-id="element-1820">
            View all services
            <ArrowRight className="w-4 h-4" data-id="element-1821" />
          </Link>
        </div>}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1822">
        <AppCard className="p-5" data-id="element-1823">
          <p className="text-[24px] font-semibold text-white" data-id="element-1824">
            {stats.totalLogs.toLocaleString()}
          </p>
          <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-1825">
            Total Logs
          </p>
        </AppCard>

        <AppCard className="p-5" data-id="element-1826">
          <p className="text-[24px] font-semibold text-white" data-id="element-1827">
            {stats.todayActivity}
          </p>
          <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-1828">
            Today's Activity
          </p>
        </AppCard>

        <AppCard className="p-5" data-id="element-1829">
          <p className="text-[24px] font-semibold text-white" data-id="element-1830">
            {stats.avgResponseTime}s
          </p>
          <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-1831">
            Avg Response Time
          </p>
        </AppCard>

        <AppCard className="p-5" data-id="element-1832">
          <p className="text-[24px] font-semibold text-[#8eff01]" data-id="element-1833">
            {stats.complianceScore}%
          </p>
          <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-1834">
            Compliance Score
          </p>
        </AppCard>
      </div>

      {/* Compact Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/[0.02] border border-white/[0.06] rounded-xl p-4" data-id="element-1835">
        <div className="flex flex-1 items-center gap-4 w-full md:w-auto" data-id="element-1836">
          <div className="relative flex-1 md:max-w-xs" data-id="element-1837">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-3" data-id="element-1838" />
            <input type="text" placeholder="Cerca..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white\\/[0.03] border border-white\\/[0.08] text-white pl-9 pr-4 py-2.5 rounded-lg text-[12px] placeholder-white\\/30 focus:outline-none focus:border-\\ [#8eff01]\\/50" data-id="element-1839" />
          </div>

          <select value={selectedEventType} onChange={e => setSelectedEventType(e.target.value)} className="bg-white\\/[0.03] border border-white\\/[0.08] text-white px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-\\[#8eff01]\\/50" data-id="element-1840">
            <option data-id="element-1841">All Events</option>
            <option data-id="element-1842">Query</option>
            <option data-id="element-1843">Response</option>
            <option data-id="element-1844">Error</option>
            <option data-id="element-1845">Policy Violation</option>
            <option data-id="element-1846">System Event</option>
          </select>

          <select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value)} className="bg-white\\/[0.03] border border-white\\/[0.08] text-white px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-\\[#8eff01]\\/50" data-id="element-1847">
            <option data-id="element-1848">All Severities</option>
            <option data-id="element-1849">Info</option>
            <option data-id="element-1850">Warning</option>
            <option data-id="element-1851">Critical</option>
          </select>
        </div>

        <div className="flex gap-2" data-id="element-1852">
          <button onClick={() => handleExport('csv')} className="p-2 text-white\\/40 hover:text-white hover:bg-white\\/[0.04] rounded-lg transition-all" title="Export CSV" data-id="element-1853">
            <Download className="w-4 h-4" data-id="element-1854" />
          </button>
          <button onClick={handleResetFilters} className="p-2 text-white\\/40 hover:text-white hover:bg-white\\/[0.04] rounded-lg transition-all" title="Reset Filters" data-id="element-1855">
            <X className="w-4 h-4" data-id="element-1856" />
          </button>
        </div>
      </div>

      {/* Audit Table */}
      <AppCard padding="none" className="overflow-hidden" data-id="element-1857">
        <div className="overflow-x-auto" data-id="element-1858">
          <table className="w-full" data-id="element-1859">
            <thead className="bg-white/[0.02] border-b border-white/[0.06]" data-id="element-1860">
              <tr data-id="element-1861">
                <th className="px-5 py-3 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1862">
                  Timestamp
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1863">
                  User
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1864">
                  Service
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1865">
                  Event Type
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1866">
                  Status
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1867">
                  Duration
                </th>
                <th className="px-5 py-3 text-right text-[10px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1868">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]" data-id="element-1869">
              {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-white/[0.04] transition-colors" data-id="element-1870">
                  <td className="px-5 py-4 whitespace-nowrap text-[12px] text-white/70" data-id="element-1871">
                    {formatDate(log.timestamp)}
                  </td>
                  <td className="px-5 py-4" data-id="element-1872">
                    <div data-id="element-1873">
                      <p className="text-[12px] font-medium text-white" data-id="element-1874">
                        {log.userName}
                      </p>
                      <p className="text-[10px] text-white/40" data-id="element-1875">
                        {log.userEmail}
                      </p>
                    </div>
                  </td>
                  <td className="px-5 py-4" data-id="element-1876">
                    <AppBadge variant="blue" data-id="element-1877">{log.serviceName}</AppBadge>
                  </td>
                  <td className="px-5 py-4" data-id="element-1878">
                    <AppBadge variant={log.eventType === 'Query' ? 'blue' : log.eventType === 'Response' ? 'green' : log.eventType === 'Error' ? 'red' : log.eventType === 'Policy Violation' ? 'red' : 'default'} data-id="element-1879">
                      {log.eventType}
                    </AppBadge>
                  </td>
                  <td className="px-5 py-4" data-id="element-1880">
                    <AppBadge variant={log.status === 'Success' ? 'green' : log.status === 'Failed' ? 'red' : 'amber'} data-id="element-1881">
                      {log.status}
                    </AppBadge>
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap text-[12px] text-white/70" data-id="element-1882">
                    {log.duration}ms
                  </td>
                  <td className="px-5 py-4 text-right" data-id="element-1883">
                    <button onClick={() => handleViewDetails(log)} className="p-2 text-white\\/40 hover:text-\\[#8eff01\\] hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1884">
                      <Eye className="w-4 h-4" data-id="element-1885" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-5 py-4 border-t border-white/[0.06] flex items-center justify-between" data-id="element-1886">
          <p className="text-[12px] text-white/40" data-id="element-1887">
            Showing {(currentPage - 1) * logsPerPage + 1}-
            {Math.min(currentPage * logsPerPage, filteredLogs.length)} of{' '}
            {filteredLogs.length} logs
          </p>
          <div className="flex gap-2" data-id="element-1888">
            <AppButton variant="secondary" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} data-id="element-1889">
              Previous
            </AppButton>
            <AppButton variant="secondary" size="sm" disabled={currentPage * logsPerPage >= filteredLogs.length} onClick={() => setCurrentPage(p => p + 1)} data-id="element-1890">
              Next
            </AppButton>
          </div>
        </div>
      </AppCard>
    </div>;
  {
    /* Floating Action Button */
  }
  {
    /* Log Details Modal */
  }
  {
    selectedLog && <LogDetailsModal log={selectedLog} activeTab={activeDetailTab} onTabChange={setActiveDetailTab} onClose={() => setSelectedLog(null)} data-id="element-1891" />;
  }
  {
    /* Retention Policy Modal */
  }
  {
    showRetentionModal && <RetentionPolicyModal onClose={() => setShowRetentionModal(false)} onSave={() => {
      setShowRetentionModal(false);
      setToast({
        message: 'Retention policy salvata',
        type: 'success'
      });
    }} data-id="element-1892" />;
  }
}